import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from '@tanstack/react-router';

import { useAuth } from '@/lib/auth';
import { useEffect, useRef, useState } from 'react';
import Loader from '@/components/Loader';
import RegistrationForm from '@/components/Auth/RegistrationForm';
import { AuthPageShell } from '@/components/Auth/AuthPageShell';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
import { GreetLogo } from '@/components/brand/GreetLogo';

const inviteFlowSchema = z.object({
  token: z.string().optional(),
  returnTo: z.string().optional(),
  ref: z.string().optional(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/register')({
  component: RegisterPage,
  validateSearch: zodValidator(inviteFlowSchema),
  head: () => {
    const title = 'Register - GreetAI';
    const description =
      'Create your GreetAI account to get started with AI voice agent platform';
    const url = 'https://app.greetai.co/register';

    return {
      title,
      meta: [
        {
          name: 'title',
          property: 'og:title',
          content: title,
        },
        {
          name: 'description',
          property: 'og:description',
          content: description,
        },
        {
          property: 'og:url',
          content: url,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          name: 'robots',
          content: 'noindex',
        },
      ],
    };
  },
});

const LEARN_REF_KEY = 'greetle_learn_ref';

function RegisterPage() {
  const { isAuthenticated, isInitialLoading, emailVerified, googleUser, logout } =
    useAuth();
  const navigate = useNavigate();
  const router = useRouter();
  const { token, returnTo, ref, redirect } = useSearch({ from: '/register' });
  // Opening an invite link starts signed out, so an existing session never swallows the invite.
  // Only when the app boots on this page: Google sign-in for the invite lands here after boot.
  const bootedHere = useRef(isInitialLoading);
  const [clearingSession, setClearingSession] = useState(
    !!token && isInitialLoading,
  );

  useEffect(() => {
    if (isInitialLoading || !clearingSession) return;
    if (bootedHere.current && (isAuthenticated || googleUser)) {
      logout().finally(() => setClearingSession(false));
    } else {
      setClearingSession(false);
    }
  }, [isInitialLoading]);

  // Load the next pages' code now so finishing signup switches screens at once
  useEffect(() => {
    router.preloadRoute({ to: '/verify-email' }).catch(() => {});
    router.preloadRoute({ to: '/dashboard' }).catch(() => {});
  }, []);

  useEffect(() => {
    if (ref) {
      try { localStorage.setItem(LEARN_REF_KEY, ref); } catch {}
    }
  }, [ref]);

  useEffect(() => {
    if (clearingSession) return;
    if (isAuthenticated && !emailVerified) {
      navigate({ to: '/verify-email' });
      return;
    }
    if (isAuthenticated) {
      navigate({ to: (returnTo || redirect || '/dashboard') as any });
    }
  }, [isAuthenticated, emailVerified, returnTo, redirect, navigate, clearingSession]);

  if (isInitialLoading || clearingSession) {
    return <Loader />;
  }

  return (
    <AuthPageShell
      logo={<GreetLogo className="h-8 w-auto" />}
    >
      {/* Google sign-in can finish while the form is open: remount so it picks up the Google defaults */}
      <RegistrationForm key={googleUser?.uid ?? 'email'} />
    </AuthPageShell>
  );
}
